import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import api from "../services/API";
import {
  verificarUsuariosAtivos as verificarUsuariosAtivosAPI,
  verificarUsuarioAtivo as verificarUsuarioAtivoAPI,
  validarToken as validarTokenAPI,
  fecharLocalizacaoEmergencia as fecharLocalizacaoEmergenciaAPI,
} from "../services/API";
import {
  inicializarPermissoesPadrao,
  resetarCachePermissoes,
} from "../utils/permissoes-inicializacao";
import { switchUnit as switchUnitAPI } from "../services/API";
import { portalLogin as portalLoginAPI } from "../services/api/portalAuth";
import ModalAvisoLogout from "../components/ModalAvisoLogout";

interface User {
  usuario_id: number;
  usuario: string;
  responsavel?: string;
  perfil: string;
  unidade?: string;
  // Portal externo (parceiro logado por CNPJ). Ausente = usuário interno.
  tipo?: "externo";
  subtipo?: "TRANSPORTADORA" | "FORNECEDOR";
  cnpj?: string;
  razao_social?: string;
}

interface UsuarioAtivo {
  usuario: string;
  responsavel: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (
    usuario: string,
    senha: string,
  ) => Promise<{ status: number; message: string }>;
  loginPortal: (
    cnpj: string,
    senha: string,
    unidade: string,
  ) => Promise<{ status: number; message: string }>;
  logout: () => void;
  switchUnit: (unidade: string) => Promise<void>;
  loading: boolean;
  verificarUsuariosAtivos: () => Promise<UsuarioAtivo[]>;
  verificarUsuarioAtivo: (usuario: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissoesInicializadas, setPermissoesInicializadas] = useState(false);

  // Estados para o modal de aviso de logout
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [logoutWarningTimer, setLogoutWarningTimer] = useState(3); // 3 segundos de aviso

  // 🔒 Função local para logout (precisa estar antes do useEffect que a usa)
  const performLogout = async () => {
    try {
      // 🚨 NOVA FUNCIONALIDADE: Fechar localizações de emergência ANTES do logout
      await fecharLocalizacoesDeEmergencia();
      await api.post("/auth/logout");
    } catch (error) {
      // Erro ao fazer logout no backend
    } finally {
      setUser(null);
      setPermissoesInicializadas(false); // Reset do estado de permissões
      resetarCachePermissoes(); // Reset do cache de permissões
      window.location.href = user?.tipo === "externo" ? "/portal/login" : "/login";
    }
  };

  // 🚨 NOVA FUNÇÃO: Fechar localizações de emergência usando rota pública
  const fecharLocalizacoesDeEmergencia = async () => {
    try {
      const localizacoesAbertas = await buscarLocalizacoesAbertasDoSistema();
      if (localizacoesAbertas.length > 0) {
        const resultados = await Promise.allSettled(
          localizacoesAbertas.map(async (ean: string) => {
            try {
              const resultado = await fecharLocalizacaoEmergenciaAPI(ean);
              return { ean, sucesso: true, resultado };
            } catch (erro) {
              return { ean, sucesso: false, erro };
            }
          }),
        );
        const sucessos = resultados.filter(
          (r) => r.status === "fulfilled" && r.value?.sucesso,
        ).length;
        const falhas = resultados.filter(
          (r) => r.status === "rejected" || !r.value?.sucesso,
        ).length;
      }
    } catch (error) {
      // Erro crítico ao fechar localizações de emergência
    }
  };

  // 🚨 FUNÇÃO AUXILIAR: Buscar localizações abertas do sistema
  const buscarLocalizacoesAbertasDoSistema = async (): Promise<string[]> => {
    try {
      const localizacoesSalvas = localStorage.getItem("localizacoesAbertas");
      if (localizacoesSalvas) {
        try {
          const localizacoes = JSON.parse(localizacoesSalvas);
          if (Array.isArray(localizacoes)) {
            const eans = localizacoes
              .map((loc: any) => loc.ean || loc)
              .filter(Boolean);
            return eans;
          }
        } catch (parseError) {
          // Erro ao fazer parse do localStorage
        }
      }
      const outrasChaves = [
        "localizacoesAbertas",
        "localizacoes_abertas",
        "gavetasAbertas",
        "gavetas_abertas",
      ];
      for (const chave of outrasChaves) {
        const valor = localStorage.getItem(chave);
        if (valor) {
          try {
            const dados = JSON.parse(valor);
            if (Array.isArray(dados)) {
              const eans = dados
                .map((item: any) => item.ean || item)
                .filter(Boolean);
              if (eans.length > 0) {
                return eans;
              }
            }
          } catch (parseError) {
            // Erro ao fazer parse da chave
          }
        }
      }
      const sessionLocalizacoes = sessionStorage.getItem("localizacoesAbertas");
      if (sessionLocalizacoes) {
        try {
          const localizacoes = JSON.parse(sessionLocalizacoes);
          if (Array.isArray(localizacoes)) {
            const eans = localizacoes
              .map((loc: any) => loc.ean || loc)
              .filter(Boolean);
            return eans;
          }
        } catch (parseError) {
          // Erro ao fazer parse do sessionStorage
        }
      }
      return [];
    } catch (error) {
      // Erro ao buscar localizações abertas
      return [];
    }
  };

  // Função auxiliar para verificar se usuário está ativo (precisa estar antes do useEffect)
  const verificarUsuarioAtivo = async (usuario: string) => {
    try {
      const response = await verificarUsuarioAtivoAPI(usuario);
      const isActive = response.isActive || false;
      return isActive;
    } catch (error) {
      return false;
    }
  };

  // 🔍 Verificação periódica consolidada - UM ÚNICO intervalo para todas as verificações
  useEffect(() => {
    if (!user) return;

    // Portal externo: JWT autocontido (7d), sem sessão única no operacional.
    // Não roda a verificação periódica interna (validarToken / usuário ativo /
    // foco da janela) — ela rejeitaria o token do parceiro e forçaria o logout.
    if (user.tipo === "externo") return;

    // ✅ ÚNICO intervalo que consolida todas as verificações
    const intervalId = setInterval(async () => {
      try {
        // Verificar token válido
        const resultado = await validarTokenAPI(user.usuario_id);
        if (!resultado.valid) {
          setShowLogoutWarning(true);
          return;
        }

        // Verificar se usuário ainda está ativo (detecta logout em outro dispositivo)
        // Multi-tenant: pula verificação (usuário está no central, não no banco operacional)
        if (!user.unidade) {
          const isActive = await verificarUsuarioAtivo(user.usuario);
          if (!isActive) {
            await fecharLocalizacoesDeEmergencia();
            await performLogout();
          }
        }
      } catch (error) {
        // Em caso de erro, mostrar aviso antes do logout
        setShowLogoutWarning(true);
      }
    }, 30000); // 30 segundos - intervalo razoável para verificação

    // Verificação quando a janela/tab ganha foco (otimizado)
    const handleFocus = async () => {
      try {
        const resultado = await validarTokenAPI(user.usuario_id);
        if (!resultado.valid) {
          setShowLogoutWarning(true);
        }
      } catch (error) {
        setShowLogoutWarning(true);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  // 🔔 Função para mostrar aviso de logout
  const showLogoutWarningModal = () => {
    setShowLogoutWarning(true);
  };

  // 🔔 Função para fechar aviso de logout - agora executa logout automaticamente
  const closeLogoutWarningModal = async () => {
    setShowLogoutWarning(false);
    // Se o modal for fechado sem confirmação, executa logout automaticamente
    await performLogout();
  };

  // 🔔 Função para executar logout após aviso
  const executeLogoutAfterWarning = async () => {
    setShowLogoutWarning(false);
    await performLogout();
  };

  // Verifica se há um token válido ao inicializar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/profile");
        const profile = response.data;

        if (profile.unidade === "central") {
          setUser({
            usuario_id: profile.usuario_id,
            usuario: profile.usuario,
            perfil: profile.perfil,
            unidade: "central",
          });
          setLoading(false);
          return;
        }

        if (profile.tipo === "externo") {
          setUser({
            usuario_id: profile.usuario_id,
            usuario: profile.razao_social || profile.cnpj,
            perfil: "externo",
            unidade: profile.unidade,
            tipo: "externo",
            subtipo: profile.subtipo,
            cnpj: profile.cnpj,
            razao_social: profile.razao_social,
          });
          setLoading(false);
          return;
        }

        setUser({
          usuario_id: profile.usuario_id,
          usuario: profile.usuario,
          responsavel: profile.responsavel,
          perfil: profile.perfil,
          unidade: profile.unidade ?? undefined,
        });

        if (!permissoesInicializadas) {
          try {
            // Adicionar timeout para evitar travamento
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("Timeout ao inicializar permissões")),
                5000,
              ),
            );

            (await Promise.race([
              inicializarPermissoesPadrao(),
              timeoutPromise,
            ])) as Promise<void>;

            setPermissoesInicializadas(true);
          } catch (error) {
            // Não falha a verificação de auth se a inicialização das permissões falhar
            console.warn(
              "Erro ao inicializar permissões (continuando):",
              error,
            );
            // Marca como inicializado mesmo em caso de erro para não tentar novamente
            setPermissoesInicializadas(true);
          }
        }
      } catch (error) {
        setUser(null);
      }

      // Sempre define loading como false, mesmo em caso de erro
      setLoading(false);
    };

    checkAuth();
    // Remover permissoesInicializadas das dependências para evitar loop infinito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (usuario: string, senha: string) => {
    try {
      const response = await api.post("/auth/login", { usuario, senha });

      if (response.data.access_token) {
        setUser({
          usuario_id: response.data.usuario_id,
          usuario: response.data.usuario,
          responsavel: response.data.responsavel,
          perfil: response.data.perfil,
          unidade: response.data.unidade,
        });

        // Inicializar permissões padrão (pula no modo central — não tem banco operacional)
        if (!permissoesInicializadas && response.data.unidade !== 'central') {
          try {
            await inicializarPermissoesPadrao();
            setPermissoesInicializadas(true);
          } catch (error) {
            // Não falha o login se a inicialização das permissões falhar
          }
        }

        return { status: 200, message: "Login realizado com sucesso!" };
      } else {
        return { status: 401, message: "Usuário ou senha inválidos" };
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        return {
          status: error.response.status,
          message: error.response.data.message,
        };
      }
      return { status: 500, message: "Erro inesperado ao tentar login." };
    }
  };

  // Login do portal externo (parceiro por CNPJ). Reusa o cookie `token`;
  // identidade sai das claims do JWT, devolvidas pelo /auth/profile.
  const loginPortal = async (cnpj: string, senha: string, unidade: string) => {
    try {
      const data = await portalLoginAPI({ cnpj, senha, unidade });
      if (data?.access_token) {
        // `usuario_id` (id do usuário externo) precisa ser truthy
        // para o PermissaoContext disparar o carregamento das permissões.
        setUser({
          usuario_id: data.usuario_id,
          usuario: data.razao_social || data.cnpj,
          perfil: "externo",
          unidade: data.unidade,
          tipo: "externo",
          subtipo: data.subtipo,
          cnpj: data.cnpj,
          razao_social: data.razao_social,
        });
        return { status: 200, message: "Login realizado com sucesso!" };
      }
      return { status: 401, message: "CNPJ ou senha inválidos" };
    } catch (error: any) {
      if (error.response?.data?.message) {
        return {
          status: error.response.status,
          message: error.response.data.message,
        };
      }
      return { status: 500, message: "Erro inesperado ao tentar login." };
    }
  };

  const logout = async () => {
    try {
      // 🚨 Fecha localizações abertas antes do logout
      await fecharLocalizacoesDeEmergencia();
      await api.post("/auth/logout");
    } catch (error) {
      // Erro ao fazer logout no backend
    } finally {
      setUser(null);
      setPermissoesInicializadas(false); // Reset do estado de permissões
      resetarCachePermissoes(); // Reset do cache de permissões
      window.location.href = user?.tipo === "externo" ? "/portal/login" : "/login";
    }
  };

  const switchUnit = async (unidade: string) => {
    const data = await switchUnitAPI(unidade);
    if (user) {
      setUser({
        ...user,
        unidade: data.unidade,
        perfil: data.perfil,
      });
    }
  };

  const verificarUsuariosAtivos = async () => {
    try {
      const response = await verificarUsuariosAtivosAPI();
      return response.activeUsers || [];
    } catch (error) {
      return [];
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    loginPortal,
    logout,
    switchUnit,
    loading,
    verificarUsuariosAtivos,
    verificarUsuarioAtivo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* Modal de aviso de logout */}
      <ModalAvisoLogout
        open={showLogoutWarning}
        onClose={closeLogoutWarningModal}
        onConfirm={executeLogoutAfterWarning}
        tempoRestante={logoutWarningTimer}
        tempoTotal={logoutWarningTimer}
      />
    </AuthContext.Provider>
  );
};
